import { useRef, type ReactNode } from "react";
import { CornerDownRight } from "lucide-react";
import { TASK_STATUS_DISPLAY_ORDER, TASK_STATUS_LABELS, type TaskStatus } from "@quovy/types";

import { Input } from "../../inputs/input";
import { ListPageToolbar } from "../../layouts/list-page/list-page-toolbar";
import { ComboboxMultiFilter, ToggleFilter, type FilterOption } from "../toolbar-filter";
import type { CursorPaginationProps } from "../data-table";
import { TasksTable, type TaskListItem } from "./tasks-table";

const STATUS_FILTER_OPTIONS: FilterOption[] = TASK_STATUS_DISPLAY_ORDER.map((value) => ({
  value,
  label: TASK_STATUS_LABELS[value],
}));

export type TaskStatusFilterValue = TaskStatus[] | "all";

interface TasksIndexProps {
  workspaceSlug: string;
  tasks: TaskListItem[];
  statusFilter: TaskStatusFilterValue;
  loading?: boolean;
  onStatusFilterChange: (value: TaskStatusFilterValue) => void;
  /**
   * Whether sub-tasks are mixed into the list. Off by default — a sub-task is
   * reachable from its parent, so listing it at the top level too is a
   * duplicate. Omit both props to hide the toggle entirely (callers that
   * already scope their own list, e.g. a resource panel).
   */
  showSubtasks?: boolean;
  onShowSubtasksChange?: (showSubtasks: boolean) => void;
  /**
   * Free-text search over the whole workspace's tasks, not the loaded page —
   * the server ranks by relevance across title, description, labels and
   * acceptance criteria. Omit both props to hide the search box (callers that
   * render a short, already-scoped list).
   */
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Prev/Next for the paginated list. Absent while a search is active. */
  cursorPagination?: CursorPaginationProps;
  /** Opens a task from keyboard row navigation. */
  onOpenTask?: (task: TaskListItem) => void;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  /** Filters rendered before Status, e.g. the page's Open / Watching / Mine view toggle. */
  leadingFilters?: ReactNode;
}

function getEmptyStateCopy(
  statusFilter: TaskStatusFilterValue,
  showSubtasks: boolean,
  search: string,
) {
  // Search is the narrowest filter, so it owns the empty state when set —
  // telling someone to "try another status" when their query simply matched
  // nothing sends them to the wrong control.
  if (search.trim().length > 0) {
    return {
      title: "No tasks match that search",
      description:
        "Try fewer or different words. Search covers task titles, descriptions, labels " +
        "and acceptance criteria across the whole workspace.",
    };
  }

  if (statusFilter === "all" || statusFilter.length === 0) {
    return {
      title: "No tasks yet",
      description: showSubtasks
        ? undefined
        : "Sub-tasks are hidden — turn on Subtasks to include them.",
    };
  }

  return {
    title: "No matching tasks",
    description: showSubtasks
      ? "Try another status or create a task in this lane."
      : "Try another status, turn on Subtasks to include them, or create a task in this lane.",
  };
}

export function TasksIndex({
  workspaceSlug,
  tasks,
  statusFilter,
  loading = false,
  onStatusFilterChange,
  showSubtasks = false,
  onShowSubtasksChange,
  search,
  onSearchChange,
  cursorPagination,
  onOpenTask,
  onRefresh,
  refreshDisabled,
  leadingFilters,
}: TasksIndexProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchValue = search ?? "";
  const searching = searchValue.trim().length > 0;
  const emptyState = getEmptyStateCopy(statusFilter, showSubtasks, searchValue);
  const selectedValues = statusFilter === "all" ? [] : statusFilter;

  const toolbar = (
    <ListPageToolbar
      {...(onSearchChange
        ? {
            search: (
              <Input
                ref={searchInputRef}
                placeholder="Search tasks…"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            ),
          }
        : {})}
      filters={
        <>
          {leadingFilters}
          <ComboboxMultiFilter
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={selectedValues}
            onValueChange={(values) => {
              onStatusFilterChange(values.length === 0 ? "all" : (values as TaskStatus[]));
            }}
            searchPlaceholder="Filter statuses…"
          />
          {onShowSubtasksChange && (
            <ToggleFilter
              label="Subtasks"
              icon={<CornerDownRight className="h-3.5 w-3.5" />}
              pressed={showSubtasks}
              onPressedChange={onShowSubtasksChange}
            />
          )}
        </>
      }
    />
  );

  // The count is what is on screen, and the wording has to say so — "500 tasks"
  // under a 50-row page would be a lie, and so would "50 tasks" for a workspace
  // that has 500.
  const paged = Boolean(cursorPagination && (cursorPagination.hasNext || cursorPagination.hasPrev));
  const footer = (
    <span>
      {searching
        ? // Search results are relevance-ordered, not newest-first like every
          // other view of this table — say so, or the ordering reads as a bug.
          `${tasks.length} ${tasks.length === 1 ? "match" : "matches"} · sorted by relevance`
        : paged
          ? `${tasks.length} on this page`
          : `${tasks.length} ${
              statusFilter === "all" || selectedValues.length === 0 ? "tasks" : "matching"
            }`}
    </span>
  );

  return (
    <TasksTable
      tasks={tasks}
      workspaceSlug={workspaceSlug}
      emptyTitle={emptyState.title}
      emptyDescription={emptyState.description}
      loading={loading}
      toolbar={toolbar}
      footer={footer}
      columnPicker
      {...(onOpenTask ? { onOpenTask } : {})}
      {...(onSearchChange ? { focusSearch: () => searchInputRef.current?.focus() } : {})}
      {...(onRefresh ? { onRefresh, refreshDisabled } : {})}
      {...(cursorPagination ? { cursorPagination } : {})}
    />
  );
}
